import type { ReactNode } from 'react';
import { cn } from '@/shared/lib';
import { styles } from '@/shared/ui/field-label/styles';

/**
 * The "Required" / "Optional" marker that sits beside a field's label.
 *
 * WHY IT IS A WORD, NOT AN ASTERISK
 * ---------------------------------
 * A bare `*` is a convention, not a word: it is unreadable to a screen reader,
 * ambiguous to anyone who has not met it, and often the one thing a colour-blind
 * user cannot distinguish. The marker here is text, at a smaller size — legible,
 * translatable, and it survives greyscale.
 *
 * WHY IT IS `aria-hidden`
 * -----------------------
 * The control already carries `required` / `aria-required`, which is the
 * machine-readable form of the same fact. Repeating it in the accessible name
 * would make every field announce "Slug Required" before its own description.
 * The marker is therefore decorative to assistive technology and load-bearing
 * only for the eye.
 *
 * WHY BOTH WORDS EXIST
 * --------------------
 * Marking only the required fields leaves the unmarked ones ambiguous — is this
 * field optional, or did the author forget the marker? Marking only the optional
 * ones has the same problem in reverse. A form with a MIX marks every field; a
 * form where everything is required marks nothing, because a marker on every
 * field is noise that carries no information.
 */
export function FieldMarker({
  required = false,
  optional = false,
}: {
  /** Renders "Required". */
  required?: boolean;
  /** Renders "Optional". Ignored when `required` is set. */
  optional?: boolean;
}): ReactNode {
  const marker = required ? 'Required' : optional ? 'Optional' : null;
  if (!marker) return null;

  return (
    <span
      className={cn(styles.marker, required ? styles.required : styles.optional)}
      aria-hidden="true"
    >
      {marker}
    </span>
  );
}
